import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import {
  KeyboardArrowDown,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { CustomerSelect } from "@/features/customers";
import {
  salesOrderDeliveryStatusColors,
  salesOrderDeliveryStatusLabels,
  salesOrderInvoiceStatusColors,
  salesOrderInvoiceStatusLabels,
  salesOrderStatusColors,
  salesOrderStatusLabels,
} from "@/features/sales-orders";
import { apiOutletClient, formatDate, formatDateTime, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const SalesOrders = () => {
  const navigate = useNavigate();
  const { outlet, changeOutlet, hasPermission, isHubOutlet } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const prevOutletRoleRef = useRef();

  // Parse buyer from URL params
  const buyer = useMemo(() => {
    const buyerParam = searchParams.get("buyer");
    if (!buyerParam) return null;
    try {
      return JSON.parse(buyerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadSalesOrders = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("sales-orders", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { referenceNumber: keyword }),
          buyerableId: buyer?.id || null,
          withCreator: true,
        },
      });
      setSalesOrders(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pesanan penjualan"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    buyer,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadSalesOrders();
  }, [loadSalesOrders]);

  // Reset buyer when outlet role changes
  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("buyer");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleBuyerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "buyer",
          JSON.stringify({ id: value.id, name: value.name })
        );
      } else {
        newParams.delete("buyer");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    handleClose();
    navigate(path);
  };

  const toolbarContent = hasPermission(PermissionEnum.CREATE_SALES_ORDERS) && (
    <>
      <Button
        fullWidth
        variant="contained"
        disabled={!isHubOutlet}
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
      >
        Tambah
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => handleMenuItemClick("create")}>
          Dari Permintaan
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("create-manual")}>
          Manual
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <PageContainer
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <AllOutletsSelect
                value={buyer}
                onChange={handleBuyerChange}
                allowedRoles={[OutletRoleEnum.RETAIL]}
                TextFieldProps={{
                  label: "Pelanggan (Retail)",
                }}
              />
            ) : (
              <CustomerSelect
                value={buyer}
                onChange={handleBuyerChange}
                TextFieldProps={{
                  label: "Pelanggan (Customer)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Penjual",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nomor referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={salesOrders}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (value) => formatDate(value),
            },
            {
              field: "buyer",
              headerName: "Pelanggan",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value?.name ?? "-",
            },
            {
              field: "createdAt",
              headerName: "Dibuat pada",
              flex: 1,
              sortable: false,
              valueGetter: (value) => formatDateTime(value),
            },
            {
              field: "creator",
              headerName: "Dibuat Oleh",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value?.name ?? "-",
            },
            {
              field: "totalDiscount",
              headerName: "Total Diskon",
              flex: 1,
              type: "number",
              sortable: false,
              valueFormatter: (value) => formatNumber(value),
            },
            {
              field: "grandTotal",
              headerName: "Pembayaran",
              flex: 1,
              type: "number",
              sortable: false,
              valueFormatter: (value) => formatNumber(value),
            },
            {
              field: "deliveryStatus",
              headerName: "Pengiriman",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={salesOrderDeliveryStatusLabels[row.deliveryStatus]}
                  color={salesOrderDeliveryStatusColors[row.deliveryStatus]}
                />
              ),
            },
            {
              field: "invoiceStatus",
              headerName: "Faktur",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={salesOrderInvoiceStatusLabels[row.invoiceStatus]}
                  color={salesOrderInvoiceStatusColors[row.invoiceStatus]}
                />
              ),
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={salesOrderStatusLabels[row.status]}
                  color={salesOrderStatusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: (params) => {
                const actions = [];
                actions.push(
                  <IconButton
                    key="view"
                    onClick={() => navigate(params.row.referenceNumber)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                );
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
